import Column from '@app/components/layout/Column'
import {
  useState,
  useRef,
  useCallback,
  useLayoutEffect,
  ReactNode,
  useEffect,
} from 'react'
import { useTranslations } from '@i18n/useLanguage'
import { TextLink } from '@app/components/core/link/TextLink'
import { Style } from 'inlines'
import { faChevronDown, faChevronUp } from '@fortawesome/pro-solid-svg-icons'

export type ContentCollapsibleProps = {
  children: ReactNode
  truncateAfter?: number
}

const lineClampStyle = (truncateAfter: number): Style => ({
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: truncateAfter,
})

export const ContentCollapsible = ({
  children,
  truncateAfter,
}: ContentCollapsibleProps) => {
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  if (!truncateAfter) {
    return <div data-testid="contentCollapsible">{children}</div>
  }

  if (mounted) {
    return (
      <ContentCollapsibleClient truncateAfter={truncateAfter}>
        {children}
      </ContentCollapsibleClient>
    )
  }

  return (
    <div style={lineClampStyle(truncateAfter)} data-testid="contentCollapsible">
      {children}
    </div>
  )
}

const ContentCollapsibleClient = ({
  children,
  truncateAfter,
}: ContentCollapsibleProps) => {
  const translations = useTranslations().app.common
  const [hasMore, setHasMore] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const textRef = useRef<HTMLDivElement>(null)

  const checkClamp = useCallback(() => {
    if (isExpanded) return
    const element = textRef.current
    if (element) {
      const hasOverflow = element.scrollHeight > element.clientHeight + 1
      setHasMore(hasOverflow)
    }
  }, [isExpanded])

  // Reset to collapsed when content changes
  useLayoutEffect(() => {
    setIsExpanded(false)
    setHasMore(false)
  }, [children])

  useLayoutEffect(() => {
    checkClamp()
    const element = textRef.current
    const ro = new ResizeObserver(() => checkClamp())
    if (element) ro.observe(element)
    window.addEventListener('resize', checkClamp)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', checkClamp)
    }
  }, [checkClamp, children])

  const toggle = () => {
    if (isExpanded) {
      setIsExpanded(false)
      // Re-check overflow after collapsing on next paint
      setTimeout(() => checkClamp(), 0)
    } else {
      setIsExpanded(true)
    }
  }

  const showToggle = hasMore || isExpanded

  return (
    <Column>
      <div
        ref={textRef}
        style={!isExpanded ? lineClampStyle(truncateAfter) : {}}
        data-testid="contentCollapsible"
      >
        {children}
      </div>
      {showToggle && (
        <TextLink
          onClick={toggle}
          icon={isExpanded ? faChevronUp : faChevronDown}
          iconPosition="right"
        >
          {isExpanded ? translations.readLess : translations.readMore}
        </TextLink>
      )}
    </Column>
  )
}
